import { useCallback } from 'react';
import { PfmeaItem } from '../types';
import { EditableField } from '../components/PfmeaEditorHelpers';
import {
    calculateActionPriority,
    createEmptyPfmeaItem,
    duplicatePfmeaItem,
    deletePfmeaItem,
    bulkSavePfmeaItems,
    submitForReview,
    approveItem,
    rejectItem,
    ChangeRecord
} from '../services/pfmeaService';
import { validatePfmeaItem } from '../utils/coachingTips';
import { FailureModeSuggestion } from '../services/pfmeaSmartSuggest';

export function usePfmeaHandlers(
    items: PfmeaItem[],
    setItems: React.Dispatch<React.SetStateAction<PfmeaItem[]>>,
    setPendingChanges: React.Dispatch<React.SetStateAction<Record<string, Partial<PfmeaItem>>>>,
    activeProjectName: string | null,
    selectedIds: Set<string>,
    setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>,
    setSaving: React.Dispatch<React.SetStateAction<boolean>>,
    cellPopout: any,
    setCellPopout: React.Dispatch<React.SetStateAction<any>>,
    popoutEditValue: string,
    setPopoutEditValue: React.Dispatch<React.SetStateAction<string>>,
    revisionModal: any,
    setRevisionModal: React.Dispatch<React.SetStateAction<any>>,
    revisionInitials: string,
    setRevisionInitials: React.Dispatch<React.SetStateAction<string>>,
    revisionReason: string,
    setRevisionReason: React.Dispatch<React.SetStateAction<string>>,
    setSelectedItemForCoaching: React.Dispatch<React.SetStateAction<PfmeaItem | null>>,
    hasPermission: (permission: string) => boolean,
    setEditingCell: React.Dispatch<React.SetStateAction<any>>,
    setActiveEditingField: React.Dispatch<React.SetStateAction<EditableField | null>>,
    setSelectedItemHistory: React.Dispatch<React.SetStateAction<any[]>>,
    setShowHistoryModal: React.Dispatch<React.SetStateAction<boolean>>,
    filteredItems: PfmeaItem[],
    setShowSmartSuggest: React.Dispatch<React.SetStateAction<boolean>>,
    showSmartSuggest: boolean,
    selectedProcessStep: any,
    setSelectedProcessStep: React.Dispatch<React.SetStateAction<any>>
) {
    // Cell Handlers
    const handleCellChange = useCallback((id: string, field: EditableField, value: any) => {
        const item = items.find(i => i.id === id);
        if (!item) return;

        const oldValue = String((item as any)[field] || '');
        const newValue = String(value);

        setItems(prev => prev.map(processedItem => {
            if (processedItem.id !== id) return processedItem;

            const updated = { ...processedItem, [field]: value };

            if (['severity', 'occurrence', 'detection'].includes(field)) {
                updated.actionPriority = calculateActionPriority(
                    updated.severity,
                    updated.occurrence,
                    updated.detection
                );
            }

            setSelectedItemForCoaching(prevSelected => {
                if (prevSelected && prevSelected.id === id) {
                    return updated;
                }
                return prevSelected;
            });

            return updated;
        }));

        setPendingChanges(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value },
        }));

        const auditableFields: EditableField[] = [
            'processStepName', 'processFunction', 'failureMode',
            'failureEffectEnd', 'failureCause', 'preventionControls', 'detectionControls'
        ];

        if (auditableFields.includes(field) && oldValue !== newValue && oldValue !== '') {
            setRevisionModal({ id, field, oldValue, newValue });
        }

    }, [items, setItems, setPendingChanges, setSelectedItemForCoaching, setRevisionModal]);

    const handleCellClick = useCallback((id: string, field: EditableField) => {
        if (!hasPermission('pfmea.edit')) return;
        setEditingCell({ id, field });
        setActiveEditingField(field);
        const item = items.find(i => i.id === id);
        if (item) setSelectedItemForCoaching(item);
    }, [items, hasPermission, setEditingCell, setActiveEditingField, setSelectedItemForCoaching]);

    const handleCellExpand = useCallback((id: string, field: EditableField) => {
        const item = items.find(i => i.id === id);
        if (!item) return;
        const rawValue = (item as any)[field];
        const value = Array.isArray(rawValue) ? rawValue.join('; ') : String(rawValue || '');
        const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
        setCellPopout({ id, field, value, label });
        setPopoutEditValue(value);
    }, [items, setCellPopout, setPopoutEditValue]);

    const handlePopoutSave = useCallback(() => {
        if (!cellPopout) return;
        const { id, field, value: originalValue } = cellPopout;

        if (popoutEditValue === originalValue) {
            setCellPopout(null);
            return;
        }

        setRevisionModal({
            id,
            field,
            oldValue: originalValue,
            newValue: popoutEditValue,
        });
    }, [cellPopout, popoutEditValue, setCellPopout, setRevisionModal]);

    // Revision Handlers
    const handleRevisionCommit = useCallback(() => {
        if (!revisionModal || !revisionInitials.trim()) {
            alert('Please provide your initials.');
            return;
        }

        const record: ChangeRecord = {
            timestamp: new Date().toISOString(),
            user: revisionInitials.trim(),
            field: revisionModal.field,
            oldValue: revisionModal.oldValue,
            newValue: revisionModal.newValue,
            comment: revisionReason.trim() || undefined,
        };

        setItems(prev => prev.map(item => {
            if (item.id !== revisionModal.id) return item;
            return {
                ...item,
                changeHistory: [...(item.changeHistory || []), record],
            };
        }));

        setRevisionModal(null);
        setCellPopout(null);
        setRevisionInitials('');
        setRevisionReason('');
    }, [revisionModal, revisionInitials, revisionReason, setItems, setRevisionModal, setCellPopout, setRevisionInitials, setRevisionReason]);

    const handleRevisionSkip = useCallback(() => {
        setRevisionModal(null);
        setCellPopout(null);
        setRevisionInitials('');
        setRevisionReason('');
    }, [setRevisionModal, setCellPopout, setRevisionInitials, setRevisionReason]);

    // Row Operations
    const handleAddRow = useCallback(() => {
        if (!activeProjectName) return;
        const newItem: PfmeaItem = {
            ...createEmptyPfmeaItem(activeProjectName),
            id: `PFMEA-NEW-${Date.now()}`,
        } as PfmeaItem;
        setItems(prev => [...prev, newItem]);
        setPendingChanges(prev => ({ ...prev, [newItem.id]: newItem }));
    }, [activeProjectName, setItems, setPendingChanges]);

    const handleDuplicateRow = useCallback(() => {
        if (selectedIds.size === 0) return;
        const lastSelectedId = Array.from(selectedIds).pop();
        if (!lastSelectedId) return;

        const itemToDuplicate = items.find(i => i.id === lastSelectedId);
        if (!itemToDuplicate) return;

        const duplicated = duplicatePfmeaItem(itemToDuplicate);
        const newItem = {
            ...duplicated,
            id: `PFMEA-NEW-${Date.now()}`,
            recommendedActions: 'None',
            createdBy: 'Current User',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        } as unknown as PfmeaItem;

        setItems(prev => [...prev, newItem]);
        setPendingChanges(prev => ({ ...prev, [newItem.id]: newItem }));
    }, [selectedIds, items, setItems, setPendingChanges]);

    const handleDeleteSelected = useCallback(async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Are you sure you want to delete ${selectedIds.size} items?`)) return;

        const newItems = items.filter(i => !selectedIds.has(i.id));
        setItems(newItems);

        try {
            await Promise.all(Array.from(selectedIds).map(id => {
                if (!id.includes('NEW')) return deletePfmeaItem(id);
                return Promise.resolve();
            }));
            setSelectedIds(new Set());
        } catch (e) {
            alert('Error deleting items');
        }
    }, [selectedIds, items, setItems, setSelectedIds]);

    // Validation & Save
    const handleSave = useCallback(async () => {
        if (!activeProjectName) return;

        let warningCount = 0;
        const errors: string[] = [];

        items.forEach(item => {
            const validation = validatePfmeaItem(item as any, {
                showValidationWarnings: true,
                showCoachingTips: true,
                showBestPractices: true,
                experienceLevel: 'intermediate'
            });

            if (validation.errors.length > 0) {
                errors.push(`Row ${item.processStepNumber}: ${validation.errors.map(e => e.message).join(', ')}`);
            }
            warningCount += validation.warnings.length;
        });

        if (errors.length > 0) {
            alert(`Cannot save due to validation errors:\n\n${errors.join('\n')}`);
            return;
        }

        let confirmMessage = 'Save all changes to database?';
        if (warningCount > 0) {
            confirmMessage += `\n\n⚠️ ${warningCount} validation warnings detected. Check Coaching Tips panel for details.`;
        }

        if (!confirm(confirmMessage)) return;

        setSaving(true);
        try {
            await bulkSavePfmeaItems(activeProjectName, items);
            setPendingChanges({});
            alert('Saved successfully!');
        } catch (err: any) {
            alert('Error saving: ' + err.message);
        } finally {
            setSaving(false);
        }
    }, [activeProjectName, items, setSaving, setPendingChanges]);

    // Workflow Actions
    const handleSubmitForReview = useCallback(async (id: string) => {
        try {
            const updated = await submitForReview(id);
            setItems(prev => prev.map(i => i.id === id ? updated : i));
        } catch (err: any) {
            alert('Error: ' + err.message);
        }
    }, [setItems]);

    const handleApprove = useCallback(async (id: string) => {
        if (!hasPermission('pfmea.approve')) {
            alert('You do not have approval permission.');
            return;
        }
        try {
            const updated = await approveItem(id);
            setItems(prev => prev.map(i => i.id === id ? updated : i));
        } catch (err: any) {
            alert('Error: ' + err.message);
        }
    }, [hasPermission, setItems]);

    const handleReject = useCallback(async (id: string) => {
        const reason = prompt('Reason for rejection:');
        if (!reason) return;
        try {
            const updated = await rejectItem(id, reason);
            setItems(prev => prev.map(i => i.id === id ? updated : i));
        } catch (err: any) {
            alert('Error: ' + err.message);
        }
    }, [setItems]);

    // Selection & UI
    const toggleSelectAll = useCallback(() => {
        if (selectedIds.size === filteredItems.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredItems.map(i => i.id)));
        }
    }, [selectedIds, filteredItems, setSelectedIds]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    }, [setSelectedIds]);

    const handleViewHistory = useCallback((item: PfmeaItem) => {
        setSelectedItemHistory(item.changeHistory || []);
        setShowHistoryModal(true);
    }, [setSelectedItemHistory, setShowHistoryModal]);

    const handleSmartSuggestClick = useCallback(() => {
        const firstSelected = selectedIds.size > 0
            ? filteredItems.find(item => selectedIds.has(item.id))
            : filteredItems[0];

        if (firstSelected) {
            setSelectedProcessStep({
                name: firstSelected.processStepName || 'Process Step',
                number: firstSelected.processStepNumber || '',
                function: firstSelected.processFunction || '',
            });
        } else {
            setSelectedProcessStep({
                name: 'New Process Step',
                number: '',
                function: '',
            });
        }
        setShowSmartSuggest(!showSmartSuggest);
    }, [selectedIds, filteredItems, showSmartSuggest, setShowSmartSuggest, setSelectedProcessStep]);

    const handleSmartSuggestAdd = useCallback((suggestion: FailureModeSuggestion) => {
        if (!activeProjectName) return;
        const newItem: PfmeaItem = {
            id: `pfmea-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            ...createEmptyPfmeaItem(activeProjectName),
            standard: (items[0]?.standard as any) || 'AIAG_VDA',
            processStepNumber: selectedProcessStep.number || `OP-${String(items.length + 1).padStart(3, '0')}`,
            processStepName: selectedProcessStep.name,
            processFunction: selectedProcessStep.function,
            processElement: suggestion.processElement,
            failureMode: suggestion.failureMode,
            failureEffectEnd: suggestion.failureEffect,
            failureEffect: suggestion.failureEffect,
            failureCause: suggestion.failureCause,
            severity: suggestion.severity,
            occurrence: suggestion.occurrence,
            detection: suggestion.detection,
            actionPriority: suggestion.actionPriority,
            preventionControls: suggestion.preventionControls,
            detectionControls: suggestion.detectionControls,
            rpn: (suggestion.severity || 1) * (suggestion.occurrence || 1) * (suggestion.detection || 1),
            status: 'draft',
        } as PfmeaItem;

        setItems(prev => [...prev, newItem]);
        setPendingChanges(prev => ({ ...prev, [newItem.id]: newItem }));
        setShowSmartSuggest(false);
    }, [activeProjectName, items, selectedProcessStep, setItems, setPendingChanges, setShowSmartSuggest]);

    return {
        handleCellChange,
        handleCellClick,
        handleCellExpand,
        handlePopoutSave,
        handleRevisionCommit,
        handleRevisionSkip,
        handleAddRow,
        handleDuplicateRow,
        handleDeleteSelected,
        handleSave,
        handleSubmitForReview,
        handleApprove,
        handleReject,
        toggleSelectAll,
        toggleSelect,
        handleViewHistory,
        handleSmartSuggestClick,
        handleSmartSuggestAdd
    };
}
